import { Menu as MenuPrimitive } from '@base-ui/react'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { POPOVER_OPTION } from '@/components/ui/control'
import { cn } from '@/lib/cn'
import { unitKindTitle } from '@/lib/legUnits'
import type { UnitKind } from '@/types/domain'

/* «+ Другой вид единицы» (S-18b) — the blue link under the unit cards that
 * offers every kind the page does not show yet, and adds that kind's card.
 *
 * A link, not a button: the only blue BUTTON on the leg page is the save bar's
 * primary action (the Locked visual decision), and this adds a card to the
 * draft — it writes nothing. Base UI's Menu for the list, portaled at
 * `z-[60]` like `MoreMenu`'s (a portaled popup is the sheets' sibling, so
 * z-index alone decides what is seen), each row the size of the control
 * (`POPOVER_OPTION`). Nothing is offered once every kind is shown, so the
 * link is not drawn at all.
 */
export function AddUnitKind({
  kinds,
  onAdd,
}: {
  /** The kinds not shown yet, in `UNIT_KINDS` order. */
  kinds: readonly UnitKind[]
  onAdd: (kind: UnitKind) => void
}) {
  const { t } = useTranslation()
  if (kinds.length === 0) return null

  return (
    <MenuPrimitive.Root modal={false}>
      <MenuPrimitive.Trigger
        className="text-text-link hover:text-text-link-hover inline-flex w-fit cursor-pointer items-center gap-1.5 text-[13px]"
      >
        <Plus size={13} aria-hidden />
        {t('cases.legs.units.addKind')}
      </MenuPrimitive.Trigger>

      <MenuPrimitive.Portal>
        <MenuPrimitive.Positioner sideOffset={4} align="start" className="z-[60]">
          <MenuPrimitive.Popup
            className={cn(
              'bg-surface border-border-subtle min-w-[200px] rounded-md border p-1',
              'shadow-[var(--shadow-overlay)] focus:outline-none',
              'origin-(--transform-origin)',
              'transition-[opacity,transform] duration-[var(--dur-fast)]',
              'ease-[var(--ease-standard)]',
              'data-starting-style:scale-[0.98] data-starting-style:opacity-0',
              'data-ending-style:scale-[0.98] data-ending-style:opacity-0',
            )}
          >
            {kinds.map((kind) => (
              <MenuPrimitive.Item
                key={kind}
                onClick={() => onAdd(kind)}
                className={cn(POPOVER_OPTION, 'text-text-body data-highlighted:bg-surface-hover')}
              >
                {unitKindTitle(t, kind)}
              </MenuPrimitive.Item>
            ))}
          </MenuPrimitive.Popup>
        </MenuPrimitive.Positioner>
      </MenuPrimitive.Portal>
    </MenuPrimitive.Root>
  )
}
